import React from "react";
import { useNavigate } from "react-router-dom";
import { useAISuggestions } from "hooks/useAISuggestions.ts";
import { isAIConfigured } from "utils/aiClient.ts";
import "./RecipeList.css";
import ReasoningPanel from "./components/ReasoningPanel.tsx";

const RecipeSuggestions: React.FC = () => {
  const { loading, error, result, requestStreaming, cancel, streaming, canRequest, reasoningText, promptText } = useAISuggestions();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem("AI_SUGGESTIONS_COLLAPSED") === "1";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem("AI_SUGGESTIONS_COLLAPSED", next ? "1" : "0");
      } catch (e) {
        // Ignore storage errors (e.g., private mode quota)
        // eslint-disable-next-line no-console
        console.debug('persist collapse failed', e);
      }
      return next;
    });
  };

  const hasResults = !!result && result.length > 0;

  const [copied, setCopied] = React.useState(false);
  const copyPrompt = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(promptText);
      }
      setCopied(true);
      globalThis.setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert('Failed to copy prompt');
    }
  };

  return (
    <section className="recipe-suggestions">
      <div className="recipe-suggestions__header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button type="button" className="recipe-suggestions__toggle" onClick={toggleCollapsed} title={collapsed ? "Expand" : "Collapse"}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform var(--transition-base)' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          <h2 className="recipe-suggestions__title">AI Dinner Suggestions</h2>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
          {hasResults || streaming || loading ? (
            <>
              <button
                type="button"
                onClick={requestStreaming}
                disabled={!canRequest || loading || streaming}
                aria-label="Refresh"
                title={!isAIConfigured() ? "Configure AI in Settings" : (loading || streaming ? "Refreshing…" : "Refresh")}
                style={{ background: 'transparent', border: 'none', padding: '4px 6px', fontSize: 16, color: '#666', cursor: 'pointer', borderRadius: 4, opacity: (!canRequest || loading || streaming) ? 0.5 : 0.85 }}
              >
                ↻
              </button>
              <button
                type="button"
                onClick={copyPrompt}
                disabled={!canRequest}
                aria-label="Copy full prompt"
                title="Copy full prompt (with pantry data)"
                style={{ background: 'transparent', border: 'none', padding: '4px 6px', fontSize: 16, color: '#666', cursor: 'pointer', borderRadius: 4, opacity: !canRequest ? 0.5 : 0.85 }}
              >
                {copied ? '✓' : '⧉'}
              </button>
              {(streaming || loading) && (
                <button
                  type="button"
                  onClick={cancel}
                  disabled={!canRequest}
                  aria-label="Cancel"
                  title="Cancel"
                  style={{ background: 'transparent', border: 'none', padding: '4px 6px', fontSize: 16, color: '#666', cursor: 'pointer', borderRadius: 4, opacity: !canRequest ? 0.5 : 0.85 }}
                >
                  ✕
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="recipe-suggestions__button"
                onClick={requestStreaming}
                disabled={!canRequest || loading || streaming}
                title={!isAIConfigured() ? "Configure AI in Settings" : undefined}
              >
                Suggest dinners
              </button>
              <button
                type="button"
                className="recipe-suggestions__button"
                onClick={copyPrompt}
                disabled={!canRequest}
                title="Copy full prompt (with pantry data)"
                style={{ background: 'transparent', border: '1px solid #ddd', color: '#444' }}
              >
                {copied ? 'Copied' : 'Copy prompt'}
              </button>
            </>
          )}
        </div>
      </div>
      {!canRequest && (
        <p style={{ color: "#a00", marginTop: 8 }}>AI is not configured. Go to Settings → AI Providers to add your key.</p>
      )}
      {error && (
        <p style={{ color: "#a00", marginTop: 8 }}>{error}</p>
      )}
      {(streaming || reasoningText) && (
        <div style={{ marginTop: 8 }}>
          <ReasoningPanel reasoningText={reasoningText ?? ""} streaming={!!streaming} initiallyOpen={false} />
        </div>
      )}
      {!collapsed && result && (
        <div className="recipe-suggestions__items" style={{ marginTop: 12 }}>
          {result.map((r, idx) => (
            <div key={idx} className="recipe-list-item" onClick={() => navigate('/recipes/ai-detail', { state: { suggestion: r } })} style={{ cursor: 'pointer' }}>
              <div className="recipe-list-item__content">
                <div className="recipe-list-item__main">
                  <h3 className="recipe-list-item__title">{r.title}</h3>
                  <div className="recipe-list-item__meta">
                    <span>{r.estimated_minutes} min</span>
                    <span> · Servings: {r.servings}</span>
                  </div>
                  <p className="recipe-list-item__description">{r.description}</p>
                  <p className="recipe-list-item__description"><strong>Ingredients:</strong> {r.ingredients_description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default RecipeSuggestions;

